import { useState, useMemo, useCallback, useEffect, memo, type ChangeEvent } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { Search as SearchIcon, Close as CloseIcon } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { useFormContext, useWatch } from "react-hook-form";
import { type FilterGroupData } from "./constants";
import {
  hasMiqaatRsvpAudienceIncluded,
  isMiqaatRsvpFilterEnvelope,
  type MiqaatRsvpFilterEnvelope,
} from "../miqaat-rsvp-filter-criteria";
import { canSyncPreviewSelection } from "./preview-selection-sync";
import {
  usePreviewRecipients,
  usePreviewMiqaatRsvpRecipients,
} from "@/containers/whatsapp-broadcasts/components/hooks";
import type { RaRecord } from "react-admin";

type PreviewRow = RaRecord & Record<string, unknown>;

type RecipientsPreviewProps = {
  filterGroup: FilterGroupData | MiqaatRsvpFilterEnvelope;
  onPreviewCount?: (count: number) => void;
  onSelectionChange?: (
    recipients: { ITS_ID: string | null; phone: string; Full_Name: string | null }[]
  ) => void;
  onPreviewed?: (previewed: boolean) => void;
  skippedIdsMap: Map<string, Set<string>>;
  setSkippedIdsMap: (updater: (prev: Map<string, Set<string>>) => Map<string, Set<string>>) => void;
  shouldFetchMap: Map<string, boolean>;
  setShouldFetchMap: (updater: (prev: Map<string, boolean>) => Map<string, boolean>) => void;
};

const RecipientsPreview = memo(
  ({
    filterGroup,
    onPreviewCount,
    onSelectionChange,
    onPreviewed,
    skippedIdsMap,
    setSkippedIdsMap,
    shouldFetchMap,
    setShouldFetchMap,
  }: RecipientsPreviewProps) => {
    const theme = useTheme();
    const { control } = useFormContext();
    const broadcastKind = useWatch({ control, name: "broadcastKind" }) as string | undefined;
    const miqaatId = useWatch({ control, name: "miqaatId" }) as string | undefined;
    const miqaatRsvpMode = broadcastKind === "MIQAAT_RSVP";

    const [validationError, setValidationError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");

    const filterKey = useMemo(() => JSON.stringify(filterGroup), [filterGroup]);

    const skippedIds = useMemo(
      () => skippedIdsMap.get(filterKey) || new Set<string>(),
      [skippedIdsMap, filterKey]
    );

    const shouldFetch = useMemo(
      () => shouldFetchMap.get(filterKey) || false,
      [shouldFetchMap, filterKey]
    );

    const standardQuery = usePreviewRecipients({
      filterCriteria: isMiqaatRsvpFilterEnvelope(filterGroup) ? null : filterGroup,
      limit: 10000,
      offset: 0,
      enabled: shouldFetch && !miqaatRsvpMode && !isMiqaatRsvpFilterEnvelope(filterGroup),
    });

    const miqaatQuery = usePreviewMiqaatRsvpRecipients({
      miqaatId,
      filterCriteria: filterGroup,
      limit: 10000,
      offset: 0,
      enabled: shouldFetch && miqaatRsvpMode,
    });

    const activeQuery = miqaatRsvpMode ? miqaatQuery : standardQuery;
    const previewData = activeQuery.data;
    const isLoading = activeQuery.isLoading;
    const isFetching = activeQuery.isFetching;
    const queryError = activeQuery.error;
    const refetch = activeQuery.refetch;

    const allData = useMemo(
      () => (previewData?.data as PreviewRow[] | undefined) || [],
      [previewData?.data]
    );
    const total = previewData?.total || 0;
    const error = queryError as (Error & { body?: { message?: string } }) | null;

    const rowSkipKey = useCallback(
      (record: PreviewRow) => {
        if (miqaatRsvpMode) {
          const p = (record.phone ?? record.WhatsApp_No ?? record.Mobile) as string | undefined;
          return p ? String(p).replace(/\D/g, "") : "";
        }
        return record.id != null
          ? String(record.id)
          : record.ITS_ID != null
            ? String(record.ITS_ID)
            : "";
      },
      [miqaatRsvpMode]
    );

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    useEffect(() => {
      if (error) {
        onPreviewed?.(false);
        return;
      }
      if (
        !canSyncPreviewSelection({
          shouldFetch,
          isLoading,
          isFetching,
          hasError: Boolean(error),
        })
      ) {
        return;
      }

      onPreviewCount?.(total);
      onPreviewed?.(true);

      if (!onSelectionChange) return;

      const selectedRecipients = allData
        .filter((recipient) => {
          const key = rowSkipKey(recipient);
          if (!key) return false;
          return !skippedIds.has(key);
        })
        .map((r) => {
          const phone = (r.phone ?? r.WhatsApp_No ?? r.Mobile) as string | undefined;
          if (phone && String(phone).trim().length > 0) {
            return {
              ...r,
              ITS_ID: (r.ITS_ID as string | null) || null,
              phone: String(phone).trim(),
              Full_Name: (r.Full_Name as string | null) || (r.name as string | null) || null,
            };
          }
          return null;
        })
        .filter((r): r is NonNullable<typeof r> => r !== null);

      onSelectionChange(selectedRecipients);
    }, [
      shouldFetch,
      isLoading,
      isFetching,
      error,
      total,
      allData,
      skippedIds,
      onPreviewCount,
      onPreviewed,
      onSelectionChange,
      rowSkipKey,
    ]);

    const handleToggleSkip = useCallback(
      (record: PreviewRow) => {
        const key = rowSkipKey(record);
        if (!key) return;
        setSkippedIdsMap((prev) => {
          const newMap = new Map(prev);
          const currentSet = new Set(newMap.get(filterKey) || []);
          if (currentSet.has(key)) {
            currentSet.delete(key);
          } else {
            currentSet.add(key);
          }
          newMap.set(filterKey, currentSet);
          return newMap;
        });
      },
      [filterKey, setSkippedIdsMap, rowSkipKey]
    );

    const handlePreview = useCallback(() => {
      if (miqaatRsvpMode && !miqaatId?.trim()) {
        setValidationError("Select a miqaat on the previous screen (broadcast type tab).");
        return;
      }
      if (miqaatRsvpMode) {
        if (
          !isMiqaatRsvpFilterEnvelope(filterGroup) ||
          !hasMiqaatRsvpAudienceIncluded(filterGroup)
        ) {
          setValidationError("Include at least one audience (ITS directory and/or Mutawatteen).");
          return;
        }
      } else if (
        !filterGroup ||
        isMiqaatRsvpFilterEnvelope(filterGroup) ||
        (!filterGroup.rules?.length && !filterGroup.groups?.length)
      ) {
        setValidationError("Please add at least one filter condition");
        return;
      }
      setValidationError(null);
      const alreadyFetching = shouldFetch;
      setShouldFetchMap((prev) => {
        const newMap = new Map(prev);
        newMap.set(filterKey, true);
        return newMap;
      });
      if (alreadyFetching) {
        void refetch();
      }
    }, [filterGroup, filterKey, setShouldFetchMap, miqaatRsvpMode, miqaatId, shouldFetch, refetch]);

    const filteredData = useMemo(() => {
      if (!searchQuery.trim()) return allData;
      const q = searchQuery.toLowerCase().trim();
      return allData.filter((r) => {
        const its = String(r.ITS_ID ?? r.itsNo ?? "").toLowerCase();
        const name = String(r.Full_Name ?? r.name ?? "").toLowerCase();
        const phone = String(r.phone ?? r.WhatsApp_No ?? r.Mobile ?? "").toLowerCase();
        return its.includes(q) || name.includes(q) || phone.includes(q);
      });
    }, [allData, searchQuery]);

    const paginatedData = useMemo(() => {
      if (!filteredData || !Array.isArray(filteredData)) return [];
      const start = page * rowsPerPage;
      const end = start + rowsPerPage;
      return filteredData.slice(start, end);
    }, [filteredData, page, rowsPerPage]);

    const handleChangePage = useCallback((_event: unknown, newPage: number) => {
      setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event: ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    }, []);

    const handleToggleAllVisible = useCallback(() => {
      const currentKeys = paginatedData.map(rowSkipKey).filter(Boolean);
      const allAreIncluded = currentKeys.every((k) => !skippedIds.has(k));

      setSkippedIdsMap((prev) => {
        const newMap = new Map(prev);
        const currentSet = new Set(newMap.get(filterKey) || []);
        if (allAreIncluded) {
          // Skip all visible
          currentKeys.forEach((k) => currentSet.add(k));
        } else {
          // Include all visible
          currentKeys.forEach((k) => currentSet.delete(k));
        }
        newMap.set(filterKey, currentSet);
        return newMap;
      });
    }, [paginatedData, rowSkipKey, skippedIds, setSkippedIdsMap, filterKey]);

    const busy = isLoading || isFetching;
    const includedCount = Math.max(0, total - skippedIds.size);

    return (
      <Box sx={{ width: "100%", minWidth: 0 }}>
        {/* Action / Trigger Bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <Button
            variant="contained"
            onClick={handlePreview}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ fontWeight: 600, textTransform: "none" }}
          >
            {busy
              ? "Loading Recipients..."
              : shouldFetch
                ? "Refresh Preview"
                : "Preview Matched Recipients"}
          </Button>

          {shouldFetch && !busy && !error && allData.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}
            >
              <Chip
                label={`${total} Matched`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
              <Chip
                label={`${includedCount} Included`}
                size="small"
                color="success"
                sx={{ fontWeight: 600 }}
              />
              {skippedIds.size > 0 && (
                <Chip
                  label={`${skippedIds.size} Skipped`}
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Stack>
          )}
        </Box>

        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        {shouldFetch && (
          <Box sx={{ mt: 1, width: "100%", minWidth: 0 }}>
            {busy && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Fetching matching audience from directory...
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {typeof error?.body?.message === "string"
                  ? error.body.message
                  : error?.message || "Failed to get recipient data"}
              </Alert>
            )}

            {!busy && !error && allData && allData.length > 0 && (
              <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                {/* Search Bar */}
                <Box
                  sx={{
                    p: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1.5,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search in preview (Name, ITS, Phone)..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setPage(0);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchQuery("")}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={{
                      width: { xs: "100%", sm: 300, md: 360 },
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      "& .MuiInputBase-root": { fontSize: "0.8125rem" },
                    }}
                  />

                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Showing {filteredData.length} of {allData.length} recipients
                  </Typography>
                </Box>

                {/* Table Container */}
                <TableContainer sx={{ maxHeight: 500, overflowX: "auto" }}>
                  <Table size="small" stickyHeader sx={{ minWidth: 500 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={
                              paginatedData.length > 0 &&
                              paginatedData.every((r) => !skippedIds.has(rowSkipKey(r)))
                            }
                            indeterminate={
                              paginatedData.some((r) => !skippedIds.has(rowSkipKey(r))) &&
                              paginatedData.some((r) => skippedIds.has(rowSkipKey(r)))
                            }
                            onChange={handleToggleAllVisible}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 110 }}>ITS ID</TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Full Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>
                          WhatsApp / Mobile
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, minWidth: 90 }}>
                          Status
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {paginatedData.map((record) => {
                        const rid = rowSkipKey(record);
                        const isSkipped = skippedIds.has(rid);
                        const phone = (record.phone ??
                          record.WhatsApp_No ??
                          record.Mobile ??
                          "") as string;
                        const itsId = (record.ITS_ID ?? record.itsNo ?? "-") as string;
                        const name = (record.Full_Name ?? record.name ?? "-") as string;

                        return (
                          <TableRow
                            key={rid || JSON.stringify(record)}
                            hover
                            sx={{
                              bgcolor: isSkipped
                                ? alpha(theme.palette.error.main, 0.05)
                                : "inherit",
                              "&:hover": {
                                bgcolor: isSkipped
                                  ? alpha(theme.palette.error.main, 0.12)
                                  : "action.hover",
                              },
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={!isSkipped}
                                onChange={() => handleToggleSkip(record)}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "0.8125rem",
                                fontFamily: "monospace",
                                fontWeight: 600,
                              }}
                            >
                              {itsId}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
                              {name}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "0.8125rem",
                                color: phone ? "text.primary" : "error.main",
                              }}
                            >
                              {phone || "No phone"}
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant={isSkipped ? "outlined" : "contained"}
                                color={isSkipped ? "error" : "primary"}
                                onClick={() => handleToggleSkip(record)}
                                sx={{
                                  fontSize: "0.72rem",
                                  py: 0.25,
                                  px: 1,
                                  minWidth: 70,
                                  textTransform: "none",
                                }}
                              >
                                {isSkipped ? "Skipped" : "Included"}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={filteredData.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </Card>
            )}

            {!busy && !error && (!allData || allData.length === 0) && (
              <Alert severity="info" sx={{ mt: 1 }}>
                No recipients found matching the filter criteria. Try broadening your rules.
              </Alert>
            )}
          </Box>
        )}
      </Box>
    );
  }
);

RecipientsPreview.displayName = "RecipientsPreview";

export default RecipientsPreview;
